/* istanbul ignore file */
import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';

import env from '@lib/envs/env';
import UserContext from '@context/User/user-context';
import ShareModalContext, { SHARETYPE } from '@context/Modal/share-modal-context';
import getProductVanityPath from '@lib/vendorVariant/getProductVanityPath';
import ButtonSvgIcon from '@components/shared/buttons/svg-icon/ButtonSvgIcon';
import PopOverAction from '@components/shared/PopOver/PopOverAction/PopOverAction';
import PopOverButton from '@components/shared/PopOver/PopOverButton/PopOverButton';
import If from '@components/shared/conditional/If/If';
import SAVE_VENDOR_VARIANT from '@graphql/VendorVariant/mutations/save-vendor-variant';
import UNSAVE_VENDOR_VARIANT from '@graphql/VendorVariant/mutations/unsave-vendor-variant';
import useAlert, { TYPES } from '@hooks/useAlert/useAlert';

import HideAt from '@components/shared/conditional/HideAt';
import ShowAt from '@components/shared/conditional/ShowAt';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import ActionModal from '@components/shared/ActionModal/ActionModal';
import Share from './svg/share.svg';
import Bookmark from './svg/bookmark.svg';
import Menu from './svg/menu.svg';
import styles from './ProductCardActions.module.scss';

interface IProductCardActionsProps {
  analytics: {
    listId?: string;
    location: string;
  };
  product: any;
  sourceBoardId?: number;
}

const ProductCardActions = ({ product, sourceBoardId, analytics }: IProductCardActionsProps) => {
  const {
    addSavedForLaterVendorVariants,
    removeSavedForLaterVendorVariants,
    savedForLaterVendorVariants
  } = useContext(UserContext);
  const { open: openShareModal } = useContext(ShareModalContext);
  const [isOpen, setIsOpen] = useState(false);
  const [saveVendorVariant] = useMutation(SAVE_VENDOR_VARIANT);
  const [unsaveVendorVariant] = useMutation(UNSAVE_VENDOR_VARIANT);
  const errorAlert = useAlert(TYPES.ERROR);
  const successAlert = useAlert(TYPES.SHOP_SUCCESS);

  const unsaveForLater = savedForLaterVendorVariants[product?.id]!!;
  const saveForLaterText = unsaveForLater ? 'Unsave for later' : 'Save for later';

  const handleShare = () => {
    openShareModal({
      modelId: product?.id,
      shareType: SHARETYPE.PRODUCT,
      url: `https://${env.DOMAIN_NAME}${getProductVanityPath(product)}`,
    });
    setIsOpen(false);
  };

  const handleSaveForLater = async () => {
    setIsOpen(false);
    try {
      if (unsaveForLater) {
        analyticsTrack('unsaved_for_later', {
          vendorVariantId: product?.id,
          boardId: sourceBoardId,
          source: analytics.location,
        });
        await unsaveVendorVariant({
          variables: {
            vendorVariantId: product?.id,
          },
        });
      } else {
        analyticsTrack('saved_for_later', {
          vendorVariantId: product?.id,
          boardId: sourceBoardId,
          source: analytics.location,
        });
        await saveVendorVariant({
          variables: {
            vendorVariantId: product?.id,
            boardId: sourceBoardId,
          },
        });
      }
    } catch (e) {
      errorAlert(`Failed to ${saveForLaterText.toLowerCase()}.`);
      return;
    }

    successAlert(unsaveForLater ? 'Unsaved for later!' : `Saved for later!`);
    if (unsaveForLater) {
      removeSavedForLaterVendorVariants(product?.id);
    } else {
      addSavedForLaterVendorVariants(product?.id);
    }
  };

  const modalActions = [
    {
      icon: (<Bookmark />),
      onClick: handleSaveForLater,
      title: saveForLaterText,
    },
    {
      icon: (<Share />),
      onClick: handleShare,
      title: 'Share',
    },
  ];

  return (
    <>
      <ShowAt breakpoint="desktop">
        <PopOverButton
          buttonClassName={ styles.Icon }
          className={styles.Icon}
          controlled
          open={ isOpen }
          setOpen={ setIsOpen }
        >
          <ul
            className={ styles.PopOverOptions}
          >
            <li>
              <PopOverAction
                icon={
                  <Bookmark />
                    }
                label={ saveForLaterText }
                onClick={ handleSaveForLater }
              />
            </li>
            <li>
              <PopOverAction
                icon={
                  <Share />
                    }
                label="Share"
                onClick={ handleShare }
              />
            </li>
          </ul>
        </PopOverButton>
      </ShowAt>
      <HideAt breakpoint="desktop">
        <ButtonSvgIcon
          onClick={ () => setIsOpen(!isOpen) }
          classNames={ styles.IconMobile }
        >
          <Menu />
        </ButtonSvgIcon>
        <If condition={isOpen}>
          <ActionModal
            onClose={() => setIsOpen(false)}
            actions={modalActions}
          />
        </If>
      </HideAt>
    </>
  );
};

export default ProductCardActions;
